import { useState, useEffect, useCallback } from 'react';
import AppLoader from 'components/general/AppLoader';
import { useSelector } from 'react-redux';
import { Button, Form, Modal, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import styles from './HintsManager.module.css';

const emptyForm = { category: '', content: '', active: true };

export default function HintsManager() {
  const { user: authUser } = useSelector(x => x.auth);
  const url = process.env.REACT_APP_URL;

  const [hints, setHints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [togglingCategory, setTogglingCategory] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingHint, setEditingHint] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [categoryInput, setCategoryInput] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authUser?.token}`,
  };

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${url}/hints/categories`, {
        headers: { Authorization: `Bearer ${authUser?.token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setCategories(data);
    } catch { /* silent */ }
  }, [url, authUser?.token]);

  const fetchHints = useCallback(async () => {
    setLoading(true);
    try {
      const endpointBase = filterCategory
        ? `${url}/hints/category/${encodeURIComponent(filterCategory)}`
        : `${url}/hints/`;
      const endpoint = `${endpointBase}${endpointBase.includes('?') ? '&' : '?'}includeInactive=1`;
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${authUser?.token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch hints');
      const data = await res.json();
      setHints(data);
    } catch (err) {
      toast.error(err.message, { position: 'bottom-left' });
    } finally {
      setLoading(false);
    }
  }, [url, authUser?.token, filterCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchHints();
  }, [fetchHints]);

  // ── Grouped display ────────────────────────────────────────────

  const groupedHints = hints.reduce((acc, hint) => {
    if (!showDisabled && !hint.active) return acc;
    const cat = hint.category || 'uncategorised';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(hint);
    return acc;
  }, {});

  const visibleHintCount = Object.values(groupedHints).reduce((acc, list) => acc + list.length, 0);

  const handleToggleCategoryVisibility = async (category) => {
    const categoryHints = hints.filter(h => (h.category || 'uncategorised') === category);
    if (categoryHints.length === 0) return;

    const hasAnyShownOnMain = categoryHints.some(h => h.showOnMain !== false);
    const nextShowOnMain = !hasAnyShownOnMain;
    const hintsToUpdate = categoryHints.filter(h => (h.showOnMain !== false) !== nextShowOnMain);
    if (hintsToUpdate.length === 0) return;

    setTogglingCategory(category);
    try {
      const responses = await Promise.all(
        hintsToUpdate.map(hint =>
          fetch(`${url}/hints/hint/${hint._id}`, {
            method: 'PATCH',
            headers: authHeaders,
            body: JSON.stringify({ showOnMain: nextShowOnMain }),
          })
        )
      );

      if (responses.some(res => !res.ok)) {
        throw new Error('Failed to update category visibility');
      }

      toast.success(
        `${category} ${nextShowOnMain ? 'enabled' : 'disabled'} for Main page typewriter`,
        { position: 'bottom-left' }
      );
      window.dispatchEvent(new Event('tm-hints-updated'));
      fetchHints();
    } catch (err) {
      toast.error(err.message, { position: 'bottom-left' });
    } finally {
      setTogglingCategory('');
    }
  };

  // ── Modal helpers ──────────────────────────────────────────────

  const openAdd = () => {
    setEditingHint(null);
    setForm(emptyForm);
    setCategoryInput('');
    setShowModal(true);
  };

  const openEdit = (hint) => {
    setEditingHint(hint);
    setForm({ category: hint.category, content: hint.content, active: hint.active });
    setCategoryInput('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHint(null);
    setCategoryInput('');
  };

  // Resolve the final category: typed input wins (case-insensitive match to existing)
  const resolveCategory = () => {
    const typed = categoryInput.trim();
    if (typed) {
      const match = categories.find(c => c.toLowerCase() === typed.toLowerCase());
      return match || typed.toLowerCase();
    }
    return form.category;
  };

  const handleAddCategory = () => {
    const resolved = resolveCategory();
    if (!resolved) return;
    setForm(prev => ({ ...prev, category: resolved }));
    setCategoryInput('');
    if (!categories.includes(resolved)) {
      setCategories(prev => [...prev, resolved].sort());
    }
  };

  const handleSave = async () => {
    const finalCategory = categoryInput.trim() ? resolveCategory() : form.category;
    if (!finalCategory) {
      toast.warning('Please select or enter a category', { position: 'bottom-left' });
      return;
    }
    if (!form.content.trim()) {
      toast.warning('Content is required', { position: 'bottom-left' });
      return;
    }
    const payload = { ...form, category: finalCategory };
    setSaving(true);
    try {
      const res = await fetch(
        editingHint ? `${url}/hints/hint/${editingHint._id}` : `${url}/hints/hint`,
        {
          method: editingHint ? 'PATCH' : 'PUT',
          headers: authHeaders,
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error('Failed to save hint');
      toast.success(editingHint ? 'Hint updated' : 'Hint added', { position: 'bottom-left' });
      closeModal();
      await fetchCategories();
      fetchHints();
    } catch (err) {
      toast.error(err.message, { position: 'bottom-left' });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${url}/hints/hint/${deleteTarget._id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to delete hint');
      toast.success('Hint deleted', { position: 'bottom-left' });
      setDeleteTarget(null);
      await fetchCategories();
      fetchHints();
    } catch (err) {
      toast.error(err.message, { position: 'bottom-left' });
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className={styles.container}>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarActions}>
          <Form.Select
            size="sm"
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All categories</option>
            {categories.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Form.Select>
          <div className={styles.toolbarButtons}>
            <Button size="sm" variant="success" onClick={openAdd} className={styles.toolbarButton}>
              <i className="bi bi-plus-lg me-1" />Add Hint
            </Button>
            <Button size="sm" variant="link" onClick={() => { fetchCategories(); fetchHints(); }} className={`${styles.toolbarButton} ${styles.toolbarIconBtn}`}>
              <i className="bi bi-arrow-clockwise" />
            </Button>
          </div>
          <Form.Check
            type="switch"
            id="show-disabled-hints"
            label="Show disabled"
            checked={showDisabled}
            onChange={e => setShowDisabled(e.target.checked)}
            className={styles.toolbarSwitch}
          />
        </div>
      </div>

      {/* Hint cards grouped by category */}
      {loading ? (
        <div className="text-center py-4"><AppLoader /></div>
      ) : visibleHintCount === 0 ? (
        <div className={styles.emptyState}>
          <i className={`bi bi-lightbulb ${styles.emptyIcon}`} />
          No hints found{filterCategory ? ` in "${filterCategory}"` : ''}.
        </div>
      ) : (
        Object.entries(groupedHints).sort(([a], [b]) => a.localeCompare(b)).map(([cat, catHints]) => {
          const allCategoryHints = hints.filter(h => (h.category || 'uncategorised') === cat);
          const isCategoryActiveForMain = allCategoryHints.some(h => h.showOnMain !== false);
          return (
            <div key={cat}>
              <div className={styles.categoryHeader}>
                <button
                  type="button"
                  onClick={() => handleToggleCategoryVisibility(cat)}
                  disabled={togglingCategory === cat}
                  className={`${styles.categoryTag} ${isCategoryActiveForMain ? styles.categoryTagActive : styles.categoryTagInactive}`}
                  title={isCategoryActiveForMain ? 'Click to exclude this category from Main typewriter' : 'Click to include this category on Main typewriter'}
                >
                  <i className="bi bi-hash" />{cat}
                </button>
                <span className={styles.categoryCount}>{catHints.length} hint{catHints.length !== 1 ? 's' : ''}</span>
              </div>
              <div className={styles.cardGrid}>
                {[...catHints]
                  .sort((a, b) => Number(!!b.active) - Number(!!a.active) || a.content.localeCompare(b.content))
                  .map(hint => (
                    <div key={hint._id} className={`${styles.hintCard} ${!hint.active ? styles.hintCardInactive : ''}`}>
                      <p className={styles.hintText}>{hint.content}</p>
                      <div className={styles.hintCardFooter}>
                        <span className={hint.active ? styles.statusActive : styles.statusInactive}>
                          {hint.active ? '● Active' : '○ Inactive'}
                        </span>
                        <div className={styles.hintCardActions}>
                          <button className={styles.actionBtn} onClick={() => openEdit(hint)} title="Edit">
                            <i className="bi bi-pencil" />
                          </button>
                          <button className={`${styles.actionBtn} ${styles.actionBtnDelete}`} onClick={() => setDeleteTarget(hint)} title="Delete">
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          );
        })
      )}

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={closeModal} centered dialogClassName={styles.modalDialog}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <i className={`bi ${editingHint ? 'bi-pencil-square' : 'bi-lightbulb'} me-2`} />
            {editingHint ? 'Edit Hint' : 'Add Hint'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <Form className="d-flex flex-column gap-3">
            <div className={styles.modalSection}>
              <Form.Label className={styles.modalLabel}>Category</Form.Label>
              <div className={styles.modalHelp}>Pick an existing category or type a new one and apply it.</div>
              <InputGroup className={styles.modalControl}>
                <Form.Select
                  value={form.category}
                  onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="">— select existing —</option>
                  {categories.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Form.Select>
                <Form.Control
                  placeholder="or type new…"
                  value={categoryInput}
                  onChange={e => setCategoryInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
                />
                <Button variant="outline-secondary" onClick={handleAddCategory} title="Apply category" className={styles.modalActionSecondary}>
                  <i className="bi bi-check2" />
                </Button>
              </InputGroup>
              {form.category && (
                <span className={styles.selectedCategoryBadge}>
                  <i className="bi bi-hash" />{form.category}
                </span>
              )}
            </div>

            <div className={styles.modalSection}>
              <Form.Label className={styles.modalLabel}>Hint text</Form.Label>
              <div className={styles.modalHelp}>Use 4-5 lines if you want the card layout to stay balanced.</div>
              <Form.Control
                as="textarea"
                rows={5}
                className={`${styles.modalControl} ${styles.modalTextArea}`}
                value={form.content}
                onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter hint text…"
              />
            </div>

            <label className={styles.toggleSwitch} htmlFor="hint-active">
              <input
                id="hint-active"
                type="checkbox"
                checked={form.active}
                onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                className={styles.toggleInput}
              />
              <span className={styles.toggleTrack}>
                <span className={styles.toggleThumb} />
              </span>
              <span className={styles.toggleText}>Active</span>
            </label>
          </Form>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="secondary" onClick={closeModal} className={styles.modalActionSecondary}>Cancel</Button>
          <Button variant="success" onClick={handleSave} disabled={saving} className={styles.modalActionPrimary}>
            {saving ? <AppLoader size="sm" inline className="me-1" /> : <i className="bi bi-floppy me-1" />}
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered size="sm" dialogClassName={styles.modalDialog}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}><i className="bi bi-exclamation-triangle me-2 text-danger" />Delete Hint</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <p className="mb-2">Are you sure you want to delete this hint?</p>
          <div className={styles.previewBox}>
            {deleteTarget?.content}
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} className={styles.modalActionSecondary}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting} className={styles.modalActionDanger}>
            {deleting ? <AppLoader size="sm" inline className="me-1" /> : <i className="bi bi-trash me-1" />}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
